import React, { useContext, useEffect, useState } from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { SessionContext } from 'contexts/session';

import AppReadyLoading from 'components/loadings/AppReadyLoading';
import Notification from 'components/notification';

interface LaunchDarklyProviderProps {
  clientSideID: string;
  context?: {
    kind: string;
    user?: {
      key: string;
      name: string;
      email: string;
    };
    ['business-account']?: {
      key: string;
      name: string;
      freeTrial: boolean;
      plan: string;
      license: string;
    };
  };
}

const LaunchDarklyProvider: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const [LDProvider, setLDProvider] = useState(null);
  const { user, account } = useContext(SessionContext);

  const buildLaunchDarklyContext = (user): LaunchDarklyProviderProps['context'] => {
    if (!user?.uaa_id) return null;
    const {
      uaa_data: {
        profile: {
          data: { first_name, last_name },
        },
      },
    } = user;

    const context: LaunchDarklyProviderProps['context'] = {
      kind: 'multi',
      user: {
        key: user.uaa_id,
        name: `${first_name} ${last_name}`,
        email: user.email,
      },
    };

    if (account?.id) {
      context['business-account'] = {
        key: account.id,
        name: account.name,
        plan: account.pricing_plan,
        license: account.license_type,
        freeTrial: account.is_free_trial,
      };
    }

    return context;
  };

  useEffect(() => {
    let mounted = true;

    const initLD = async () => {
      if (!user?.uaa_id) return;

      const context = buildLaunchDarklyContext(user);
      if (!context) return;

      try {
        const provider = await asyncWithLDProvider({
          clientSideID: process.env.REACT_APP_DARKLY_CLIENT_SIDE_KEY,
          context,
        });

        if (mounted) {
          setLDProvider(() => provider);
        }
      } catch (error) {
        Notification({
          type: 'error',
          text: 'LaunchDarkly error',
          description: 'There was an error initializing LaunchDarkly',
        });
      }
    };

    initLD();

    return () => {
      mounted = false;
    };
  }, [user?.uaa_id]);

  if (!LDProvider) return <AppReadyLoading />;

  return <LDProvider>{children}</LDProvider>;
};

export default LaunchDarklyProvider;
